import { createFileRoute, redirect } from '@tanstack/react-router'
import { workspaceMember } from '@/lib/queries/workspace-members'
import { workspace } from '@/lib/queries/workspaces'
import { decodeId, encodeId } from '@/lib/short-uuid'
import { WorkspaceLayout } from '@/features/workspaces/layout'
import { rememberLastWorkspace } from '@/features/workspaces/lib/last-workspace'
import {
  getWorkspaceAccess,
  needsWorkspaceMembership,
} from '@/features/workspaces/lib/workspace-access'

export const Route = createFileRoute(
  '/_authenticated/org/$orgId/workspace/$workspaceId'
)({
  params: {
    parse: (rawParams: { workspaceId: string }) => {
      try {
        return { workspaceId: decodeId(rawParams.workspaceId) }
      } catch {
        return { workspaceId: rawParams.workspaceId }
      }
    },
    stringify: (params: { workspaceId: string }) => ({
      workspaceId: encodeId(params.workspaceId),
    }),
  },
  staticData: { sidebar: 'workspace' },
  beforeLoad: async ({ params, context }) => {
    let workspaceData
    try {
      workspaceData = await context.queryClient.query({
        ...workspace.findOne(params.workspaceId),
        staleTime: 'static',
      })

      if (workspaceData.org_id !== params.orgId) {
        throw new Error('Workspace does not belong to org')
      }
    } catch {
      throw redirect({
        to: '/org/$orgId',
        params: { orgId: params.orgId },
        replace: true,
      })
    }

    const accessInput = {
      workspace: workspaceData,
      profileId: context.currentProfile.id,
      isOrgAdmin: context.isOrgAdmin,
    }
    const isWorkspaceMember = needsWorkspaceMembership(accessInput)
      ? await context.queryClient.query({
          ...workspaceMember.isMember(
            params.workspaceId,
            context.currentProfile.id
          ),
          staleTime: 'static',
        })
      : false
    const access = getWorkspaceAccess({ ...accessInput, isWorkspaceMember })

    return {
      canManageWorkspace: access.canManage,
      canDeleteWorkspace: access.canDelete,
      isPrivateWorkspace: workspaceData.visibility === 'private',
    }
  },
  onEnter: ({ context, params }) => {
    rememberLastWorkspace(context.currentProfile.id, {
      orgId: params.orgId,
      workspaceId: params.workspaceId,
    })
  },
  component: WorkspaceLayout,
})
